/**
 * Regras da venda Canopus no app (sem interface): o que vem do contato, os
 * códigos do AFV e o que falta antes de enviar. As mesmas checagens rodam no
 * servidor (api-server canopus/http.mjs) — aqui é para o vendedor ver antes.
 */
import type { Contact } from '../types';
import type { ClienteCanopus } from '@/lib/canopus-api';
import { getContactDetails } from '../contacts/contact-details';

/** Estados civis como o AFV escreve (o servidor converte para o código). */
export const ESTADOS_CIVIS = [
  'SOLTEIRO', 'CASADO', 'DIVORCIADO', 'VIUVO', 'UNIÃO ESTAVEL', 'SEPARADO JUDICIALMENTE',
  'SOLTEIRO(A) COM UNIÃO ESTÁVEL', 'DIVORCIADO(A) COM UNIÃO ESTÁVEL', 'VIÚVO(A) COM UNIÃO ESTÁVEL', 'OUTRO',
];

export const FORMAS_CONTATO = [
  { valor: 1, rotulo: 'Local de trabalho' },
  { valor: 2, rotulo: 'Domicílio' },
  { valor: 3, rotulo: 'Telefone' },
  { valor: 4, rotulo: 'Meios eletrônicos (e-mail, chat, site)' },
];

export const COMPROVANTES_RENDA = [
  { valor: 1, rotulo: 'Holerite' },
  { valor: 2, rotulo: 'Imposto de renda' },
  { valor: 3, rotulo: 'Extrato bancário' },
  { valor: 4, rotulo: 'Pró-labore' },
  { valor: 5, rotulo: 'Declaração' },
];

export const DOCUMENTOS_PF = ['CPF', 'DOCUMENTO DE IDENTIDADE', 'COMPROVANTE DE ENDEREÇO', 'COMPROVANTE DE RENDA'];

const ESTADO_COM_CONJUGE = /^(CASADO|UNI[ÃA]O EST[ÁA]VEL|CONCUBINATO)|COM UNI[ÃA]O EST[ÁA]VEL/i;
export const precisaConjuge = (estadoCivil: string) => ESTADO_COM_CONJUGE.test(estadoCivil || '');

const ESTADO_DO_CONTATO: Record<string, string> = {
  single: 'SOLTEIRO', married: 'CASADO', divorced: 'DIVORCIADO', widowed: 'VIUVO', other: 'OUTRO',
};

const soDigitos = (v: unknown) => String(v ?? '').replace(/\D/g, '');

/** "1993-12-03" → "03/12/1993"; já em DD/MM/AAAA passa direto. */
export function dataBr(v: unknown): string {
  const s = String(v ?? '').trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return /^\d{2}\/\d{2}\/\d{4}$/.test(s) ? s : '';
}

/**
 * Preenche o cliente com o que o contato já tem. Os campos que só a Canopus
 * pede (RG, profissão, mãe, cargo público…) ficam em details.canopus, gravados
 * na venda anterior desse contato.
 */
export function clienteDoContato(contato: Contact | null | undefined): ClienteCanopus {
  const d = getContactDetails(contato) as Record<string, any>;
  const salvo = (d.canopus || {}) as Partial<ClienteCanopus>;
  const fone = soDigitos(d.phone);
  const genero = String(d.gender || '').toLowerCase();
  return {
    nome: String(d.name || '').trim(),
    cpf: soDigitos(d.cpf),
    nascimento: dataBr(d.birth_date),
    sexo: genero.startsWith('f') ? 'F' : 'M',
    rg: '', rg_emissao: '', rg_orgao: '', profissao: '', nome_mae: '',
    estado_civil: ESTADO_DO_CONTATO[String(d.marital_status || '')] || '',
    email: String(d.email || '').trim(),
    celular: fone,
    telefone: fone,
    cep: soDigitos(d.zipcode),
    numero: String(d.address_number || '').trim(),
    complemento: String(d.address_complement || '').trim(),
    nacionalidade: 'BRASILEIRO',
    uf_nascimento: '', cidade_nascimento: '',
    renda: d.monthly_income != null && d.monthly_income !== '' ? String(d.monthly_income) : '',
    pessoa_politicamente_exposta: false,
    ocupou_cargo_publico: false,
    parente_cargo_publico: false,
    conta_bancaria: 'nao_desejo_informar',
    documentos_pf: [],
    ...salvo,
  };
}

/** O que falta para a Canopus aceitar (mesmas regras do servidor). */
export function faltando(c: ClienteCanopus): string[] {
  const f: string[] = [];
  const vazio = (v: unknown) => v === undefined || v === null || String(v).trim() === '';
  const rotulos: [keyof ClienteCanopus, string][] = [
    ['nome', 'nome'], ['cpf', 'CPF'], ['nascimento', 'nascimento'], ['rg', 'RG'], ['rg_emissao', 'emissão do RG'],
    ['rg_orgao', 'órgão emissor'], ['estado_civil', 'estado civil'], ['profissao', 'profissão'], ['email', 'e-mail'],
    ['celular', 'celular'], ['telefone', 'telefone'], ['cep', 'CEP'], ['numero', 'número'],
    ['nacionalidade', 'nacionalidade'], ['uf_nascimento', 'UF de nascimento'], ['cidade_nascimento', 'cidade de nascimento'],
    ['renda', 'renda'], ['nome_mae', 'nome da mãe'],
  ];
  for (const [k, r] of rotulos) if (vazio(c[k])) f.push(r);
  if (!vazio(c.cpf) && soDigitos(c.cpf).length !== 11) f.push('CPF com 11 dígitos');
  if (!vazio(c.email) && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c.email)) f.push('e-mail válido');
  if (!vazio(c.nascimento) && !dataBr(c.nascimento)) f.push('nascimento em DD/MM/AAAA');
  if (!vazio(c.rg_emissao) && !dataBr(c.rg_emissao)) f.push('emissão do RG em DD/MM/AAAA');
  if (c.documentos_pf.length === 0) f.push('ao menos um documento PF');
  if (precisaConjuge(c.estado_civil)) {
    const cj = c.conjuge;
    for (const [k, r] of [['nome', 'nome'], ['cpf', 'CPF'], ['rg', 'RG'], ['nascimento', 'nascimento'], ['rg_orgao', 'órgão emissor'], ['profissao', 'profissão']] as const) {
      if (vazio(cj?.[k])) f.push(`cônjuge: ${r}`);
    }
  }
  return f;
}

/**
 * Chaves do que pedir em "Informações complementares" ao escolher o contato.
 * As declarações (cargo público, conta bancária) entram enquanto o contato não
 * tiver passado por uma venda Canopus: não dá para assumir "não" por ele.
 */
export function chavesFaltando(c: ClienteCanopus, jaConfirmouDeclaracoes: boolean): Set<string> {
  const vazio = (v: unknown) => v === undefined || v === null || String(v).trim() === '';
  const s = new Set<string>();
  for (const k of ['nome', 'cpf', 'nascimento', 'rg', 'rg_emissao', 'rg_orgao', 'estado_civil', 'profissao', 'email',
    'celular', 'telefone', 'cep', 'numero', 'nacionalidade', 'uf_nascimento', 'cidade_nascimento', 'renda', 'nome_mae'] as const) {
    if (vazio(c[k])) s.add(k);
  }
  if (c.documentos_pf.length === 0) s.add('documentos_pf');
  if (!jaConfirmouDeclaracoes) s.add('declaracoes');
  if (precisaConjuge(c.estado_civil)) {
    const cj = c.conjuge;
    if (!cj || ['nome', 'cpf', 'rg', 'nascimento', 'rg_orgao', 'profissao'].some((k) => vazio((cj as any)[k]))) s.add('conjuge');
  }
  return s;
}

/** O contato já passou por uma venda Canopus (tem os dados guardados)? */
export const jaTemFichaCanopus = (contato: Contact | null | undefined) =>
  !!(getContactDetails(contato) as Record<string, any>).canopus;

/** O que guardar no contato para a próxima venda (só os campos da Canopus). */
export function extrasParaContato(c: ClienteCanopus): Partial<ClienteCanopus> {
  const { rg, rg_emissao, rg_orgao, profissao, nome_mae, nome_pai, nacionalidade, uf_nascimento, cidade_nascimento,
    estado_civil, pessoa_politicamente_exposta, ocupou_cargo_publico, parente_cargo_publico, conta_bancaria,
    cep_comercial, numero_comercial, complemento_comercial, conjuge } = c;
  return { rg, rg_emissao, rg_orgao, profissao, nome_mae, nome_pai, nacionalidade, uf_nascimento, cidade_nascimento,
    estado_civil, pessoa_politicamente_exposta, ocupou_cargo_publico, parente_cargo_publico, conta_bancaria,
    cep_comercial, numero_comercial, complemento_comercial, conjuge };
}
